import * as Tooltip from '@radix-ui/react-tooltip';
import type {
  BatchStatus,
  BatchType,
  PartnerBatchStatusResponse,
} from '@reach/shared-types/partner-batches';
import { useQuery } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Info,
  Loader2,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import { partnerApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, type SelectOption } from '../ui/select';
import { BatchDetailModal } from './BatchDetailModal';
import { BatchStatusBadge } from './BatchStatusBadge';

const getBatchTypeLabel = (batchType: BatchType): string => {
  switch (batchType) {
    case 'PARTNER_RESOURCE_JSON_INGEST':
      return 'Data Import';
    case 'PARTNER_RESOURCE_BATCH_DELETE':
      return 'Bulk Delete';
    case 'PARTNER_RESOURCE_BATCH_PATCH':
      return 'Bulk Update';
    default:
      return batchType;
  }
};

const statusOptions: SelectOption[] = [
  { value: '', label: 'All Statuses' },
  { value: 'processing', label: 'Processing' },
  { value: 'success', label: 'Success' },
  { value: 'partial_success', label: 'Partial Success' },
  { value: 'failure', label: 'Failed' },
];

const batchTypeOptions: SelectOption[] = [
  { value: '', label: 'All Types' },
  {
    value: 'PARTNER_RESOURCE_JSON_INGEST',
    label: 'Data Import',
  },
  { value: 'PARTNER_RESOURCE_BATCH_DELETE', label: 'Bulk Delete' },
  { value: 'PARTNER_RESOURCE_BATCH_PATCH', label: 'Bulk Update' },
];

export const BatchMonitoringTab: React.FC = () => {
  // Default to 'all' for "All Tenants"
  const [tenantId, setTenantId] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<BatchStatus | ''>('');
  const [batchTypeFilter, setBatchTypeFilter] = useState<BatchType | ''>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<{
    [page: number]: string | undefined;
  }>({});
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [selectedBatchBusinessId, setSelectedBatchBusinessId] = useState<
    string | null
  >(null);
  const [copiedBatchId, setCopiedBatchId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<
    'createdAt' | 'startedAt' | 'completedAt'
  >('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const navigate = useNavigate();
  const { addToast } = useToast();

  // Fetch tenants for the filter dropdown (only need id and name)
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => partnerApi.getTenants(false, { fields: ['id', 'name'] }),
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Check if we have any processing batches to determine if we should poll
  const isAllTenants = tenantId === 'all';
  const { data, isLoading, error } = useQuery({
    queryKey: [
      'batches',
      tenantId,
      debouncedSearchTerm,
      statusFilter,
      batchTypeFilter,
      currentPage,
      pageCursors[currentPage],
      sortField,
      sortDirection,
    ],
    queryFn: () => {
      if (isAllTenants) {
        // Use partner endpoint for cross-tenant queries
        return partnerApi.getPartnerBatches({
          limit: 20,
          cursor: pageCursors[currentPage],
          batchType: batchTypeFilter || undefined,
          status: statusFilter || undefined,
          sortBy: sortField,
          sortDirection,
        });
      } else {
        // Use tenant-specific endpoint
        return partnerApi.getBatches(tenantId, {
          limit: 20,
          cursor: pageCursors[currentPage],
          batchType: batchTypeFilter || undefined,
          sortBy: sortField,
          sortDirection,
        });
      }
    },
    enabled: !!tenantId, // Only enable when tenantId is set (either 'all' or a valid tenant ID)
    staleTime: 30000,
    gcTime: 300000,
    // Poll every 10 seconds if there are processing batches
    refetchInterval: query => {
      if (!tenantId) return false; // Don't poll if no tenant selected
      const batches = query.state.data?.data?.results || [];
      const hasProcessing = batches.some(
        batch => batch.status === 'processing',
      );
      return hasProcessing ? 10000 : false;
    },
  });

  // Store cursor for next page
  useEffect(() => {
    if (data?.data?.pagination.cursor) {
      setPageCursors(prev => ({
        ...prev,
        [currentPage + 1]: data.data.pagination.cursor || undefined,
      }));
    }
  }, [data, currentPage]);

  // Reset pagination when filters or sorting change
  useEffect(() => {
    setCurrentPage(1);
    setPageCursors({});
  }, [
    debouncedSearchTerm,
    statusFilter,
    batchTypeFilter,
    tenantId,
    sortField,
    sortDirection,
  ]);

  const batches: PartnerBatchStatusResponse[] = data?.data?.results || [];
  const pagination = data?.data?.pagination;

  // Filter batches client-side based on filters (batchType is now filtered server-side)
  const filteredBatches = batches.filter(batch => {
    if (statusFilter && batch.status !== statusFilter) return false;
    // batchTypeFilter is now handled server-side, so we don't filter it here
    if (
      debouncedSearchTerm &&
      !batch.batchId.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    )
      return false;
    return true;
  });

  // Create tenant options for the select component
  const tenantOptions: SelectOption[] = [
    { value: 'all', label: 'All Tenants' },
    ...(tenantsData?.data?.results?.map(tenant => ({
      value: tenant.id,
      label: tenant.name,
    })) || []),
  ];

  // Helper function to truncate batch ID
  const truncateBatchId = (batchId: string): string => {
    if (batchId.length <= 5) return batchId;
    return `...${batchId.slice(-5)}`;
  };

  // Handle copy batch ID
  const handleCopyBatchId = (batchId: string) => {
    navigator.clipboard.writeText(batchId);
    setCopiedBatchId(batchId);
    setTimeout(() => setCopiedBatchId(null), 2000);
    addToast({
      variant: 'success',
      title: 'Copied!',
      description: 'Batch ID copied to clipboard',
    });
  };

  // Handle sorting
  const handleSort = (field: 'createdAt' | 'startedAt' | 'completedAt') => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default direction desc
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Get sort icon for a column
  const getSortIcon = (field: 'createdAt' | 'startedAt' | 'completedAt') => {
    if (sortField !== field) {
      return <ChevronUp className="w-3 h-3 text-gray-300" />;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-3 h-3 text-gray-600" />
    ) : (
      <ChevronDown className="w-3 h-3 text-gray-600" />
    );
  };

  return (
    <Tooltip.Provider>
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Select
            options={tenantOptions}
            value={tenantId}
            onChange={setTenantId}
            placeholder="All Tenants"
            searchable={true}
            className="min-w-[200px]"
          />
          <Input
            type="text"
            placeholder="Search by batch ID..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="sm:flex-1"
          />
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={value => setStatusFilter(value as BatchStatus | '')}
            placeholder="All Statuses"
            className="min-w-[140px]"
            disabled={!tenantId}
          />
          <Select
            options={batchTypeOptions}
            value={batchTypeFilter}
            onChange={value => setBatchTypeFilter(value as BatchType | '')}
            placeholder="All Types"
            className="min-w-[160px]"
            disabled={!tenantId}
          />
        </div>

        {/* Table */}
        {!tenantId ? (
          <div className="text-center py-12 text-gray-600">
            Please select a tenant to view batches.
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-lg text-red-700">
            Failed to load batches. Please try again.
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-600">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading batches...
          </div>
        ) : filteredBatches.length === 0 ? (
          <div className="text-center py-12 text-gray-600">
            {batches.length === 0
              ? 'No batches found.'
              : 'No batches match your filters.'}
          </div>
        ) : (
          <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                      {/* Actions - narrow column */}
                    </th>
                    {isAllTenants && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tenant
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Batch ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Schema Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('createdAt')}>
                      <div className="flex items-center gap-1">
                        <span>Created</span>
                        {getSortIcon('createdAt')}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('startedAt')}>
                      <div className="flex items-center gap-1">
                        <span>Started</span>
                        {getSortIcon('startedAt')}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('completedAt')}>
                      <div className="flex items-center gap-1">
                        <span>Completed</span>
                        {getSortIcon('completedAt')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredBatches.map(batch => {
                    // Find tenant name for display
                    const tenantName = batch.businessId
                      ? tenantsData?.data?.results?.find(
                          t => t.id === batch.businessId,
                        )?.name || batch.businessId
                      : null;

                    return (
                      <tr key={batch.batchId} className="hover:bg-gray-50">
                        <td className="px-2 py-4 whitespace-nowrap text-center">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedBatchId(batch.batchId);
                              setSelectedBatchBusinessId(
                                batch.businessId || null,
                              );
                            }}
                            className="text-gray-400 hover:text-gray-600 transition-colors">
                            <Info className="h-4 w-4" />
                          </button>
                        </td>
                        {isAllTenants && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {tenantName || '—'}
                            </div>
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <button
                                type="button"
                                onClick={() => handleCopyBatchId(batch.batchId)}
                                className="text-sm font-mono text-gray-900 hover:text-blue-600 cursor-pointer flex items-center gap-1">
                                {truncateBatchId(batch.batchId)}
                                {copiedBatchId === batch.batchId ? (
                                  <Check className="h-3 w-3 text-green-500" />
                                ) : (
                                  <Copy className="h-3 w-3 opacity-50" />
                                )}
                              </button>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                              <Tooltip.Content
                                side="top"
                                sideOffset={5}
                                className="z-50 px-2 py-1 text-xs text-white bg-gray-900 rounded shadow-md">
                                {batch.batchId}
                                <Tooltip.Arrow className="fill-gray-900" />
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {getBatchTypeLabel(batch.batchType)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {batch.schemaName && batch.schemaDefinitionId ? (
                            <button
                              type="button"
                              onClick={() => {
                                navigate(
                                  `/dashboard/settings/data-integration?schema=${batch.schemaDefinitionId}`,
                                );
                              }}
                              className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                              {batch.schemaName}
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <BatchStatusBadge status={batch.status} />
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {batch.progress.processedRecords ?? 0} /{' '}
                              {batch.progress.totalRecords ?? 0}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {batch.createdAt ? (
                            <div className="text-xs text-gray-500 leading-tight">
                              {new Date(batch.createdAt).toLocaleDateString(
                                'en-US',
                                {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                },
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {batch.startedAt ? (
                            <div className="text-xs text-gray-500 leading-tight">
                              {new Date(batch.startedAt).toLocaleDateString(
                                'en-US',
                                {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                },
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {batch.completedAt ? (
                            <div className="text-xs text-gray-500 leading-tight">
                              {new Date(batch.completedAt).toLocaleDateString(
                                'en-US',
                                {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                },
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  {(() => {
                    const startItem = (currentPage - 1) * 20 + 1;
                    const endItem = startItem + filteredBatches.length - 1;
                    const total = pagination?.total;

                    if (isLoading) return 'Loading...';
                    if (filteredBatches.length === 0) return 'No batches found';

                    // If total is available, show it; otherwise just show the range
                    if (total !== null && total !== undefined && total > 0) {
                      return `Viewing ${startItem}-${endItem} of ${total} total batches`;
                    } else {
                      return `Viewing ${startItem}-${endItem} batches`;
                    }
                  })()}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1 || isLoading}>
                    Previous
                  </Button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage(p => (pagination?.hasNextPage ? p + 1 : p))
                    }
                    disabled={!pagination?.hasNextPage || isLoading}>
                    {isLoading ? 'Loading…' : 'Next'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Batch Detail Modal */}
        {selectedBatchId && (selectedBatchBusinessId || tenantId !== 'all') && (
          <BatchDetailModal
            batchId={selectedBatchId}
            tenantId={selectedBatchBusinessId || tenantId}
            isOpen={!!selectedBatchId}
            onClose={() => {
              setSelectedBatchId(null);
              setSelectedBatchBusinessId(null);
            }}
          />
        )}
      </div>
    </Tooltip.Provider>
  );
};
